/**
 * Emporia cart runtime.
 * Required hooks: [data-emporia-cart-drawer], [data-emporia-cart-dialog],
 * [data-emporia-cart-open/close/backdrop], form[data-emporia-cart-add],
 * form[data-emporia-product-form], or form.emporia-product-form, and
 * [data-emporia-cart-quantity/remove].
 * Emits: emporia:cart:open, emporia:cart:close, emporia:cart:added,
 * emporia:cart:updated, and emporia:cart:error on document.
 */

(() => {
  'use strict';

  if (window.EmporiaCart) return;

  const ADD_FORM_SELECTOR =
    'form[data-emporia-cart-add], form[data-emporia-product-form], form.emporia-product-form';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class EmporiaCartController {
    constructor() {
      this.returnFocus = null;
      this.quantityTimers = new WeakMap();
      this.enterTimer = 0;

      document.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      document.addEventListener('change', this.onChange.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));
    }

    get root() {
      return document.querySelector('[data-emporia-cart-drawer]');
    }

    get isOpen() {
      return Boolean(this.root && !this.root.hidden);
    }

    get routes() {
      const root = this.root;
      const localeRoot = window.Shopify?.routes?.root || '/';
      const withLocaleRoot = (path) =>
        `${localeRoot.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;

      return {
        cart: root?.dataset.emporiaCartUrl || withLocaleRoot('cart'),
        add: root?.dataset.emporiaCartAddUrl || withLocaleRoot('cart/add'),
        change: root?.dataset.emporiaCartChangeUrl || withLocaleRoot('cart/change'),
        json: root?.dataset.emporiaCartJsonUrl || withLocaleRoot('cart.js'),
        sectionId: root?.dataset.emporiaCartSectionId || 'emporia-cart-drawer'
      };
    }

    emit(name, detail = {}) {
      document.dispatchEvent(
        new CustomEvent(name, {
          bubbles: false,
          detail
        })
      );
    }

    onClick(event) {
      const openTrigger = event.target.closest('[data-emporia-cart-open]');
      if (openTrigger && this.root) {
        event.preventDefault();
        this.open();
        return;
      }

      if (
        event.target.closest('[data-emporia-cart-close]') ||
        event.target.closest('[data-emporia-cart-backdrop]')
      ) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeButton = event.target.closest('[data-emporia-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.changeLine(removeButton.dataset.lineKey, 0, 'remove');
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(ADD_FORM_SELECTOR)) {
        return;
      }

      if (
        event.submitter &&
        event.submitter.name !== 'add' &&
        !event.submitter.matches('[data-emporia-cart-add-submit], [data-add-to-cart]')
      ) {
        return;
      }

      if (!this.root || form.target === '_blank') return;

      event.preventDefault();
      this.add(form, event.submitter);
    }

    onChange(event) {
      const input = event.target.closest('[data-emporia-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) return;

      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      input.value = String(quantity);

      window.clearTimeout(this.quantityTimers.get(input));
      const timer = window.setTimeout(() => {
        this.changeLine(input.dataset.lineKey, quantity, 'quantity');
      }, 250);
      this.quantityTimers.set(input, timer);
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const dialog = this.root?.querySelector('[data-emporia-cart-dialog]');
      if (!dialog) return;

      const focusable = [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.hidden && element.getClientRects().length > 0
      );

      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open({ setReturnFocus = true, emit = true, animate = true } = {}) {
      const root = this.root;
      if (!root) return false;

      if (setReturnFocus) this.returnFocus = document.activeElement;

      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      this.setScrollLock(true);

      if (animate) {
        root.classList.add('emporia-cart-drawer--entering');
        window.clearTimeout(this.enterTimer);
        this.enterTimer = window.setTimeout(() => {
          this.root?.classList.remove('emporia-cart-drawer--entering');
        }, 400);
      }

      const dialog = root.querySelector('[data-emporia-cart-dialog]');
      const closeButton = root.querySelector('[data-emporia-cart-close]');
      window.requestAnimationFrame(() => (dialog || closeButton)?.focus());

      if (emit) this.emit('emporia:cart:open');
      return true;
    }

    close({ restoreFocus = true, emit = true } = {}) {
      const root = this.root;
      if (!root || root.hidden) return;

      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      root.classList.remove('emporia-cart-drawer--entering');
      this.setScrollLock(false);

      if (restoreFocus && this.returnFocus instanceof HTMLElement) {
        this.returnFocus.focus();
      }
      this.returnFocus = null;

      if (emit) this.emit('emporia:cart:close');
    }

    /**
     * Brava's app.css keeps `overflow-x: hidden` on html and body, so an inline
     * `body { overflow: hidden }` does not stop the page behind the drawer from
     * scrolling. Both elements need the lock class.
     */
    setScrollLock(isLocked) {
      [document.documentElement, document.body].forEach((element) => {
        element.classList.toggle('emporia-cart-open', isLocked);
      });
    }

    async add(form, submitter) {
      const routes = this.routes;
      const formData = new FormData(form);
      if (submitter?.name && !formData.has(submitter.name)) {
        formData.append(submitter.name, submitter.value);
      }
      formData.set('sections', routes.sectionId);
      formData.set('sections_url', routes.cart);
      const distinctId =
        window.posthog && typeof window.posthog.get_distinct_id === 'function'
          ? window.posthog.get_distinct_id()
          : '';
      if (distinctId && /^[A-Za-z0-9._:-]{8,200}$/.test(distinctId)) {
        formData.set('attributes[_ph_distinct_id]', distinctId);
      }

      let mutationCompleted = false;
      this.returnFocus = submitter instanceof HTMLElement ? submitter : form;
      this.setBusy(true, form);

      try {
        const response = await this.fetchJson(this.jsonEndpoint(routes.add), {
          method: 'POST',
          body: formData
        });
        mutationCompleted = true;

        const cart = await this.finishUpdate(response.sections, 'add', true);
        this.emit('emporia:cart:added', { cart, item: response });
      } catch (error) {
        this.handleError(error, 'add');
        if (mutationCompleted) {
          window.location.assign(routes.cart);
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      } finally {
        this.setBusy(false, form);
      }
    }

    async changeLine(lineKey, quantity, source) {
      if (!lineKey) return;

      const routes = this.routes;
      let mutationCompleted = false;
      this.setBusy(true);

      try {
        const response = await this.fetchJson(this.jsonEndpoint(routes.change), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            quantity,
            sections: [routes.sectionId],
            sections_url: routes.cart
          })
        });
        mutationCompleted = true;
        await this.finishUpdate(response.sections, source, true);
      } catch (error) {
        this.handleError(error, source);
        window.location.assign(routes.cart);
      } finally {
        if (!mutationCompleted) this.setBusy(false);
      }
    }

    jsonEndpoint(route) {
      return route.endsWith('.js') ? route : `${route}.js`;
    }

    async fetchJson(url, options = {}) {
      const headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers
      };
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers
      });

      let payload;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        const message =
          payload?.description || payload?.message || `Cart request failed (${response.status})`;
        throw new Error(message);
      }

      return payload;
    }

    async finishUpdate(sections, source, openAfterUpdate) {
      const routes = this.routes;
      const suppliedMarkup = sections?.[routes.sectionId];
      const [cart, markup] = await Promise.all([
        this.fetchJson(routes.json),
        suppliedMarkup ? Promise.resolve(suppliedMarkup) : this.fetchSection(routes)
      ]);

      this.replaceDrawer(markup, openAfterUpdate);
      this.updateSharedCartState(cart);
      this.announce(cart);
      if (source !== 'add') this.validateOrderLimits(cart);
      this.emit('emporia:cart:updated', { cart, source });
      return cart;
    }

    validateOrderLimits(cart) {
      if (window.aod_JQuery && typeof window.aodValidate === 'function') {
        window.aodValidate(cart, true, false);
      }
    }

    async fetchSection(routes) {
      const url = new URL(routes.cart, window.location.origin);
      url.searchParams.set('sections', routes.sectionId);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error(`Cart section request failed (${response.status})`);
      }

      const sections = await response.json();
      if (!sections?.[routes.sectionId]) {
        throw new Error('Cart section response was empty');
      }
      return sections[routes.sectionId];
    }

    replaceDrawer(markup, openAfterUpdate) {
      const current = this.root;
      if (!current || !markup) throw new Error('Cart drawer markup is unavailable');

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const replacement = parsed.querySelector('[data-emporia-cart-drawer]');
      if (!replacement) throw new Error('Cart drawer markup is invalid');

      const wasOpen = this.isOpen;
      current.replaceWith(replacement);

      if (wasOpen || openAfterUpdate) {
        this.open({
          setReturnFocus: false,
          emit: !wasOpen,
          animate: !wasOpen
        });
      }
    }

    updateSharedCartState(cart) {
      const subtotal = this.root
        ?.querySelector('[data-emporia-cart-subtotal]')
        ?.textContent.trim() || this.root?.dataset.emporiaCartTotalValue;

      document
        .querySelectorAll('.js-cart-item-count, [data-emporia-cart-count]')
        .forEach((element) => {
          element.textContent = String(cart.item_count);
          element.dataset.count = String(cart.item_count);
        });

      document.querySelectorAll('[data-emporia-cart-label]').forEach((element) => {
        element.setAttribute(
          'aria-label',
          `${element.dataset.emporiaCartLabel}, ${cart.item_count}`
        );
      });

      if (subtotal !== undefined) {
        document
          .querySelectorAll('.js-cart-item-total, [data-emporia-cart-total]')
          .forEach((element) => {
            element.textContent = subtotal;
          });
      }
    }

    announce(cart) {
      const liveRegion = this.root?.querySelector('[data-emporia-cart-live]');
      const title = this.root
        ?.querySelector('#EmporiaCartDrawerTitle')
        ?.textContent.trim();
      if (liveRegion) liveRegion.textContent = `${title || ''}: ${cart.item_count}`;
    }

    setBusy(isBusy, scope = this.root) {
      const container = scope || this.root;
      if (isBusy) {
        const errorRegion = this.root?.querySelector('[data-emporia-cart-error]');
        if (errorRegion) {
          errorRegion.hidden = true;
          errorRegion.textContent = '';
        }
      }
      container?.setAttribute('aria-busy', String(isBusy));
      container
        ?.querySelectorAll('button, input')
        .forEach((control) => {
          if (isBusy) {
            control.dataset.emporiaPreviouslyDisabled = String(control.disabled);
            control.disabled = true;
          } else {
            control.disabled = control.dataset.emporiaPreviouslyDisabled === 'true';
            delete control.dataset.emporiaPreviouslyDisabled;
          }
        });
    }

    handleError(error, source) {
      const liveRegion = this.root?.querySelector('[data-emporia-cart-live]');
      const errorRegion = this.root?.querySelector('[data-emporia-cart-error]');
      if (liveRegion) liveRegion.textContent = error?.message || 'Cart update failed';
      if (errorRegion) {
        errorRegion.textContent = error?.message || 'Cart update failed';
        errorRegion.hidden = false;
      }
      this.emit('emporia:cart:error', { error, source });
      console.error('[Emporia cart]', error);
    }
  }

  window.EmporiaCart = new EmporiaCartController();
})();
